import React, { useId, useState } from 'react';
import { api, MagentoConfigAuditReport, ConfigRemediationResult } from '../api';
import { Callout, Section, Tabs } from './primitives';
import { SshStat, SshStats } from './SshStats';
import { StatusBadge } from './ui';

interface MagentoConfigAuditCardProps {
  environmentId: string;
}

type AuditCheck = MagentoConfigAuditReport['checks'][number];

/** The button of a proposed fix says what it changes. */
function remediationLabel(action: string): string {
  switch (action) {
    case 'set_indexers_schedule':
      return 'Switch indexers to update by schedule';
    case 'enable_static_signing':
      return 'Enable static signing';
    case 'flush_config_cache':
      return 'Flush the configuration cache';
    default:
      return 'Apply the safe fix';
  }
}

export const MagentoConfigAuditCard: React.FC<MagentoConfigAuditCardProps> = ({ environmentId }) => {
  const [report, setReport] = useState<MagentoConfigAuditReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'wiring' | 'environment' | 'optimizations'>('wiring');
  const [remediatingAction, setRemediatingAction] = useState<string | null>(null);
  const [remediationResult, setRemediationResult] = useState<ConfigRemediationResult | null>(null);
  const tabsId = useId();
  const panelId = `${tabsId}-panel`;

  const runAudit = async () => {
    setLoading(true);
    setError(null);
    setRemediationResult(null);
    try {
      const res = await api.getMagentoConfigAudit(environmentId);
      setReport(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The configuration audit failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemediate = async (action: string) => {
    setRemediatingAction(action);
    setError(null);
    try {
      const res = await api.remediateMagentoConfig(environmentId, action);
      setRemediationResult(res);
      // Automatically refresh the audit report to reflect fixed state
      const updated = await api.getMagentoConfigAudit(environmentId);
      setReport(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The fix could not be applied.');
    } finally {
      setRemediatingAction(null);
    }
  };

  // Group checks into level 1 and level 2
  const envChecks = report?.checks.filter(
    (c) => c.category === 'php' || c.category === 'server' || c.category === 'infrastructure'
  ) || [];
  const wiringChecks = report?.checks.filter(
    (c) => c.category !== 'php' && c.category !== 'server' && c.category !== 'infrastructure'
  ) || [];

  const checkList = (checks: AuditCheck[], label: string, withFix: boolean) => (
    <ul className="status-list status-list--stacked" aria-label={label}>
      {checks.map((c, i) => (
        <li key={`${c.name}-${i}`} className="status-list__item">
          <span className="status-list__name">
            <span>{c.name}</span> <span className="ssh-meta">{c.category.replace(/_/g, ' ')}</span>
          </span>
          <StatusBadge kind="auditResult" value={c.status} />
          <div className="status-list__detail">
            {c.message}
            {c.observed_value ? (
              <div className="muted">
                Found <code>{c.observed_value}</code>
              </div>
            ) : null}
            {c.recommendation ? <div className="ssh-advice">{c.recommendation}</div> : null}
            {withFix && c.remediation_action ? (
              <div className="ssh-actions">
                <button
                  type="button"
                  onClick={() => handleRemediate(c.remediation_action!)}
                  disabled={remediatingAction !== null}
                  className="button button--secondary button--small"
                >
                  {remediatingAction === c.remediation_action ? 'Applying' : remediationLabel(c.remediation_action)}
                </button>
              </div>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <Section
      title="Magento configuration"
      testId="magento-config-audit"
      actions={
        <button
          type="button"
          onClick={runAudit}
          disabled={loading || remediatingAction !== null}
          className="button button--secondary"
        >
          {loading ? 'Auditing the configuration' : report ? 'Run the audit again' : 'Run configuration audit'}
        </button>
      }
    >
      {error ? (
        <Callout tone="danger" title="That did not work">
          {error}
        </Callout>
      ) : null}

      {remediationResult ? (
        <Callout
          tone="success"
          title="The fix was applied"
        >
          <p>
            <code>{remediationResult.action}</code> finished in <span className="tabular-nums">{remediationResult.duration_ms} ms</span>.
          </p>
          <pre className="ssh-output">{remediationResult.output}</pre>
        </Callout>
      ) : null}

      {report ? (
        <>
          <SshStats>
            <SshStat label="Overall" value={`${report.overall_score}/100`} />
            <SshStat label="Caching" value={`${report.caching_score}/100`} />
            <SshStat label="Infrastructure" value={`${report.infrastructure_score}/100`} />
            <SshStat label="Performance" value={`${report.performance_score}/100`} />
          </SshStats>

          <dl className="fact-list">
            <div className="fact-list__row">
              <dt>Magento mode</dt>
              <dd>{report.magento_mode || 'Unknown'}</dd>
            </div>
            <div className="fact-list__row">
              <dt>Session storage</dt>
              <dd>{report.session_backend || 'Unknown'}</dd>
            </div>
            <div className="fact-list__row">
              <dt>Default and page cache</dt>
              <dd>
                {report.default_cache_backend || 'files'} and {report.page_cache_backend || 'files'}
              </dd>
            </div>
            <div className="fact-list__row">
              <dt>Static content signing</dt>
              <dd>
                <StatusBadge
                  kind="auditResult"
                  value={report.static_signing_enabled ? 'passed' : 'warning'}
                  label={report.static_signing_enabled ? 'Enabled' : 'Disabled'}
                />
              </dd>
            </div>
            <div className="fact-list__row">
              <dt>Indexers</dt>
              <dd>
                {report.realtime_indexer_count > 0 ? (
                  <>
                    <StatusBadge kind="auditResult" value="warning" label={`${report.realtime_indexer_count} update on save`} />{' '}
                    <span className="muted">Switch them to update by schedule.</span>
                  </>
                ) : (
                  <StatusBadge kind="auditResult" value="passed" label={`All ${report.scheduled_indexer_count} update by schedule`} />
                )}
              </dd>
            </div>
          </dl>

          {/* Views inside this section, so the quieter secondary bar. */}
          <Tabs
            label="Configuration audit"
            variant="secondary"
            idBase={tabsId}
            panelId={panelId}
            value={activeTab}
            onChange={setActiveTab}
            options={[
              { value: 'wiring', label: 'Magento wiring', count: wiringChecks.length },
              { value: 'environment', label: 'Runtime', count: envChecks.length },
              {
                value: 'optimizations',
                label: 'Safe fixes',
                count: report.findings.length > 0 ? report.findings.length : undefined,
              },
            ]}
          />

          <div role="tabpanel" id={panelId} aria-labelledby={`${tabsId}-${activeTab}`}>
            {activeTab === 'wiring' ? (
              wiringChecks.length > 0 ? (
                checkList(wiringChecks, 'Magento wiring checks', true)
              ) : (
                <p className="quiet-empty">No wiring checks were reported.</p>
              )
            ) : null}

            {activeTab === 'environment' ? (
              envChecks.length > 0 ? (
                checkList(envChecks, 'Runtime checks', false)
              ) : (
                <p className="quiet-empty">No runtime checks were reported.</p>
              )
            ) : null}

            {activeTab === 'optimizations' ? (
              report.findings.length > 0 ? (
                <ul className="status-list status-list--stacked" aria-label="Safe fixes">
                  {report.findings.map((f, i) => (
                    <li key={`${f.title}-${i}`} className="status-list__item">
                      <span className="status-list__name">{f.title}</span>
                      <StatusBadge kind="severity" value={f.severity} />
                      <div className="status-list__detail">
                        {f.description}
                        <div>
                          <strong>Production impact:</strong> {f.impact}
                        </div>
                        {f.remediation_action ? (
                          <div className="ssh-actions">
                            <button
                              type="button"
                              onClick={() => handleRemediate(f.remediation_action!)}
                              disabled={remediatingAction !== null}
                              className="button button--secondary button--small"
                            >
                              {remediatingAction === f.remediation_action ? 'Applying' : remediationLabel(f.remediation_action)}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="quiet-empty">No safe fixes</p>
              )
            ) : null}
          </div>
        </>
      ) : (
        <p className="quiet-empty">Not audited yet</p>
      )}
    </Section>
  );
};
